import { DateTime } from 'luxon';
import { ScheduleOfValues } from 'ontraccr-common';

import Analytics from '../../helpers/Analytics';
import {
  CREATE_PROJECT,
  GET_ALL_PROJECTS,
  ARCHIVE_PROJECT,
  UPDATE_PROJECT,
  DELETE_PROJECT,
  GET_PROJECT_EQUIPMENT,
  GET_PROJECT_EQUIPMENT_TABLE_COLUMNS,
  UPDATE_PROJECT_EQUIPMENT_TABLE_COLUMNS,
  UPDATE_PROJECT_CUSTOMER,
  GET_PROJECT_SCHEDULE_OF_VALUES,
  UPDATE_PROJECT_SCHEDULE_OF_VALUES,
  UPDATE_PROJECT_SCHEDULE_OF_VALUES_ROW,
  DELETE_PROJECT_SCHEDULE_OF_VALUES_ROW,
  SUBMIT_PROJECT_SCHEDULE_OF_VALUES,
  SWAP_ROW_PROJECT_SCHEDULE_OF_VALUES,
  GET_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
  UPDATE_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
  ADD_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
  DELETE_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
  UPDATE_INDIVIDUAL_PROJECT,
  UPDATE_PROJECT_FROM_COST_TRACKING,
  GET_ALL_PROJECT_COSTCODE_DETAILS,
  GET_PROJECT_ATTACHMENTS,
  ADD_PROJECT_PROGRESS_HISTORY_FILES,
  GET_SCHEDULE_OF_VALUE_TEMPLATES,
  CREATE_SCHEDULE_OF_VALUE_TEMPLATE,
  UPDATE_SCHEDULE_OF_VALUE_TEMPLATE,
  DELETE_SCHEDULE_OF_VALUE_TEMPLATE,
  APPLY_SCHEDULE_OF_VALUE_TEMPLATE,
  GET_PROJECT_FILE_TEMPLATES,
  ADD_PROJECT_FILE_TEMPLATE,
  UPDATE_PROJECT_FILE_TEMPLATE,
  DELETE_PROJECT_FILE_TEMPLATE,
  UPDATE_PROJECT_PROGRESS_FILTERS,
  GET_PROJECT_GROUPS,
  CREATE_PROJECT_GROUP,
  UPDATE_PROJECT_GROUP,
  DELETE_PROJECT_GROUP,
  GET_PROJECT_GROUP_NOTES,
  ADD_PROJECT_GROUP_NOTE,
  GET_PROJECT_TYPES,
  GET_PROJECT_COST_UPDATES,
  GET_SCHEDULE_OF_VALUE_SECTIONS,
  CREATE_SCHEDULE_OF_VALUE_SECTION,
  UPDATE_SCHEDULE_OF_VALUE_SECTION,
  DELETE_SCHEDULE_OF_VALUE_SECTION,
  GET_PROGRESS_SUBCONTRACT_UPDATES,
  GET_SUB_CONTRACTS,
  UPDATE_PROGRESS_SUBCONTRACT,
  UPDATE_PROGRESS_SUBCONTRACT_ROW,
  DELETE_PROGRESS_SUBCONTRACT_UPDATE,
  SWAP_ROW_PROGRESS_SUBCONTRACT,
  GET_PROJECT_CUSTOM_FIELD_TEMPLATE,
  UPDATE_PROJECT_CUSTOM_FIELD_TEMPLATE,
  GET_PROJECT_CUSTOM_DATA,
  GET_PROJECT_NOTES,
  ADD_PROJECT_NOTE,
  GEOCODE_PROJECT,
  EDIT_PROJECT_SCHEDULE_OF_VALUES_PAST_BILL,
  GET_PROJECT_EQUIPMENT_BUCKET_OVERRIDE_TREE,
  GET_PROJECT_LABELS,
  GET_MY_PROJECT_TAGS,
  MARK_PROJECT_TAGS_AS_READ,
  GET_MY_PROJECT_GROUP_TAGS,
  MARK_PROJECT_GROUP_TAGS_AS_READ,
  SUBMIT_PROJECT_SCHEDULE_OF_VALUES_INVOICE_HOLDBACK,
  UPDATE_PROJECT_FILTERS,
  GET_USER_PROJECT_FILTER_VIEWS,
  CREATE_USER_PROJECT_FILTER_VIEW,
  UPDATE_USER_PROJECT_FILTER_VIEW,
  DELETE_USER_PROJECT_FILTER_VIEW,
  FAVOURITE_USER_PROJECT_FILTER_VIEW,
  SET_FILTERED_PROJECT_IDS,
  GET_PROJECT_CUSTOM_CATEGORY_ESTIMATES,
  MERGE_PHASES_FOR_PROJECT,
  DELETE_PHASE_AND_COSTCODES,
  CREATE_MANY_COSTCODE,
  MERGE_COSTCODE_ESTIMATES,
} from '../../state/actionTypes';
import ProjectsService from './projects.service';
import { createFolders, uploadFiles } from '../../files/state/files.actions';
import Permissions from '../../auth/Permissions';
import { decorateFormWithFiles } from '../../forms/formHelpers';
import { counters, counterWasRecentlyCalled } from '../../helpers/counters';

const {
  projects: projectCounters,
} = counters;

export const createProject = (project, fileMap = {}, divisionName = '') => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/Create');
  const fileMapValues = Object.values(fileMap);
  if (fileMapValues.length) {
    Analytics.track('Project/Files/Upload');
    const { name } = project;
    const prefix = `Projects/${name}`;
    const files = [];
    const folders = [];
    fileMapValues.forEach((file) => {
      const { type, fullPath } = file;
      if (type === 'folder') {
        folders.push(`${prefix}${fullPath}/`);
      } else {
        files.push(file);
      }
    });

    const { divisionId } = project;

    if (folders.length) {
      const createdFolders = await dispatch(createFolders({ names: folders, divisionId }));
      if (!createdFolders) return false;
    }
    if (files.length) {
      const divisionPrefix = divisionName ? `${divisionName}/` : '';
      const payload = files.map((file) => ({
        jsFileObject: file.jsFileObject,
        path: `${divisionPrefix}${prefix}${file.parent}`,
      }));

      const uploadedFiles = await dispatch(uploadFiles(payload));
      if (!uploadedFiles) return false;
    }
  }
  const { data: newProject } = await ProjectsService.create(project, fileMap);
  if (!newProject) return false;
  dispatch({
    type: CREATE_PROJECT,
    payload: {
      project: newProject,
    },
  });
  return newProject;
};

export const getProjects = (query) => async (dispatch) => {
  if (counterWasRecentlyCalled('projects.getProjects')) return true;
  const { data: projects } = await ProjectsService.getAll(query);
  projectCounters.getProjects = DateTime.local();
  if (!projects) {
    projectCounters.getProjects = null;
    return false;
  }
  dispatch({
    type: GET_ALL_PROJECTS,
    payload: {
      projects,
    },
  });
  return true;
};

export const getProjectAttachments = (id) => async (dispatch) => {
  const { data } = await ProjectsService.getProjectAttachments(id);
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_ATTACHMENTS,
    payload: {
      projectAttachments: data,
    },
  });
  return true;
};

export const archiveProject = (id, active = false) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/Archive');
  const { data } = await ProjectsService.archive(id, active);
  if (!data) return false;
  dispatch({
    type: ARCHIVE_PROJECT,
    payload: {
      projectId: id,
      active,
    },
  });
  return true;
};

export const bulkArchiveProjects = (ids, { password }, active = false) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/BulkArchive', { active, count: ids.length });
  const { data, err } = await ProjectsService.bulkArchive(ids, { password, active });
  if (err || !data) return false;
  (data.updatedIds || []).forEach((projectId) => {
    dispatch({
      type: ARCHIVE_PROJECT,
      payload: {
        projectId,
        active,
      },
    });
  });
  return true;
};

export const updateProject = (id, payload) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/Edit');
  const { data } = await ProjectsService.update(id, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_PROJECT,
    payload: {
      projectId: id,
      data,
    },
  });
  return true;
};
export const updateIndividualProject = ({
  projectId,
  payload,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/Edit');
  const { data } = await ProjectsService.update(projectId, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_INDIVIDUAL_PROJECT,
    payload: {
      data,
    },
  });
  return true;
};

// eslint-disable-next-line max-len
export const updateProjectFromCostTracking = (projectId, payload) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  const { data } = await ProjectsService.updateProjectFromCostTracking(projectId, payload);
  if (!data) return false;

  const {
    project,
    phases = [],
    customCategoryEstimates = [],
    removedCostcodeIds = [],
    addedCostcodes = [],
    updatedCostcodes = [],
    editedPhaseId = null,
    removedPhaseId = null,
  } = data;

  if (project) {
    dispatch({
      type: UPDATE_PROJECT_FROM_COST_TRACKING,
      payload: {
        projectId,
        project,
        customCategoryEstimates,
      },
    });
  }

  if (phases?.length > 0) {
    dispatch({
      type: MERGE_PHASES_FOR_PROJECT,
      payload: { projectId, phases },
    });
  }

  if (removedCostcodeIds?.length > 0 || removedPhaseId) {
    dispatch({
      type: DELETE_PHASE_AND_COSTCODES,
      payload: {
        projectId,
        phaseId: removedPhaseId ?? null,
        removedCostcodeIds,
      },
    });
  }

  if (addedCostcodes?.length > 0) {
    dispatch({
      type: CREATE_MANY_COSTCODE,
      payload: { costcodes: addedCostcodes },
    });
  }

  if (updatedCostcodes?.length > 0) {
    dispatch({
      type: MERGE_COSTCODE_ESTIMATES,
      payload: {
        updatedCostcodes,
        editedPhaseId: editedPhaseId ?? null,
      },
    });
  }

  return true;
};

export const deleteProject = (id) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/Delete');
  const { data } = await ProjectsService.delete(id);
  if (!data) return false;
  dispatch({
    type: DELETE_PROJECT,
    payload: {
      id,
    },
  });
  return true;
};

export const bulkDeleteProjects = (ids, { password }) => async (dispatch) => {
  if (!(Permissions.has('PROJECTS_WRITE'))) return false;
  Analytics.track('Project/BulkDelete', { count: ids.length });
  const { data, err } = await ProjectsService.bulkDelete(ids, { password });
  if (err || !data) return false;
  (data.deletedIds || []).forEach((id) => {
    dispatch({
      type: DELETE_PROJECT,
      payload: { id },
    });
  });
  return true;
};

export const getProjectEquipment = (shouldIgnoreCounter = false) => async (dispatch) => {
  if (!shouldIgnoreCounter && counterWasRecentlyCalled('projects.getProjectEquipment')) return true;
  projectCounters.getProjectEquipment = DateTime.local();
  const { data = {} } = await ProjectsService.getEquipment();
  if (!data) {
    projectCounters.getProjectEquipment = null;
    return false;
  }
  dispatch({
    type: GET_PROJECT_EQUIPMENT,
    payload: data,
  });
  return true;
};

export const getProjectEquipmentTableColumns = (projectTypeId) => async (dispatch) => {
  const { data } = await ProjectsService.getProjectEquipmentTableColumns(projectTypeId);
  if (!data) return false;

  dispatch({
    type: GET_PROJECT_EQUIPMENT_TABLE_COLUMNS,
    payload: {
      projectTypeId,
      data,
    },
  });
  return data;
};

export const updateProjectEquipmentTableColumns = (projectTypeId, payload) => async (dispatch) => {
  const { data } = await ProjectsService.updateProjectEquipmentTableColumns(projectTypeId, payload);
  if (!data) return false;

  dispatch({
    type: UPDATE_PROJECT_EQUIPMENT_TABLE_COLUMNS,
    payload: {
      projectTypeId,
      data,
    },
  });
  return true;
};

export const updateProjectsCustomer = (payload) => async (dispatch) => {
  const { data } = await ProjectsService.updateCustomer(payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_PROJECT_CUSTOMER,
    payload,
  });
  return true;
};

export const getProjectScheduleOfValues = ({
  projectId,
  templateId,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_READ'))) return false;
  const hasIds = !!projectId || !!templateId;
  if (!hasIds && counterWasRecentlyCalled('projects.getProjectScheduleOfValues')) return true;
  if (!hasIds) projectCounters.getProjectScheduleOfValues = DateTime.local();
  const { data } = templateId
    ? await ProjectsService.getScheduleOfValuesTemplateData(templateId)
    : await ProjectsService.getScheduleOfValues(projectId);

  if (!data) {
    if (!hasIds) projectCounters.getProjectScheduleOfValues = null;
    return false;
  }

  dispatch({
    type: GET_PROJECT_SCHEDULE_OF_VALUES,
    payload: {
      scheduleOfValues: data,
    },
  });
  return true;
};

export const updateProjectScheduleOfValues = ({
  projectId,
  templateId,
  payload,
  projectName = '',
  files = [],
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;

  const { data } = await ProjectsService.updateScheduleOfValues({
    projectId,
    templateId,
    payload,
    projectName,
    files,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: UPDATE_PROJECT_SCHEDULE_OF_VALUES,
    payload: {
      scheduleOfValues: data,
    },
  });
  return true;
};

export const editProjectScheduleOfValuesPastBill = ({
  projectId,
  payload,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;

  const { data } = await ProjectsService.editScheduleOfValuesPastBill({
    projectId,
    payload,
  });

  if (!data) return false;

  dispatch({
    type: EDIT_PROJECT_SCHEDULE_OF_VALUES_PAST_BILL,
    payload: {
      scheduleOfValues: data,
    },
  });
  return true;
};

export const submitProjectScheduleOfValues = (
  projectId,
  payload,
  projectName = '',
  files = [],
) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data: scheduleOfValues } = await ProjectsService.submitScheduleOfValues(
    projectId,
    payload,
    projectName,
    files,
  );

  if (!scheduleOfValues) {
    return false;
  }

  dispatch({
    type: SUBMIT_PROJECT_SCHEDULE_OF_VALUES,
    payload: {
      scheduleOfValues,
    },
  });
  return true;
};

export const swapRowsProjectScheduleOfValues = ({
  projectId,
  templateId,
  updatedRows,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;

  const { data: scheduleOfValues } = await ProjectsService.swapRowsScheduleOfValues({
    projectId,
    templateId,
    payload: { updatedRows },
  });

  if (!scheduleOfValues) {
    return false;
  }

  dispatch({
    type: SWAP_ROW_PROJECT_SCHEDULE_OF_VALUES,
    payload: {
      scheduleOfValues,
    },
  });
  return true;
};

export const updateScheduleOfValuesRow = ({
  projectId,
  templateId,
  row,
  payload,
}) => (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;

  const updatedRow = ScheduleOfValues.getUpdatedRow({ row, payload, isProgress: false });

  dispatch({
    type: UPDATE_PROJECT_SCHEDULE_OF_VALUES_ROW,
    payload: {
      key: `${projectId || templateId}.${row.rowId}`,
      value: updatedRow,
    },
  });

  return true;
};

export const deleteProjectScheduleOfValueRow = ({
  projectId,
  templateId,
  rowId,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.deleteScheduleOfValuesRow({
    projectId,
    templateId,
    rowId,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: DELETE_PROJECT_SCHEDULE_OF_VALUES_ROW,
    payload: {
      scheduleOfValues: data,
    },
  });
  return true;
};

export const getProjectScheduleOfValuePDFRows = ({
  projectId,
  templateId,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.getProjectScheduleOfValuePDFRows({
    projectId,
    templateId,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: GET_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
    payload: {
      scheduleOfValuePDFRows: data,
    },
  });
  return true;
};

export const updateProjectScheduleOfValuePDFRows = ({
  projectId,
  templateId,
  rowId,
  payload,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.updateProjectScheduleOfValuePDFRows({
    projectId,
    templateId,
    rowId,
    payload,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: UPDATE_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
    payload: {
      scheduleOfValuePDFRows: data,
    },
  });
  return true;
};

export const addProjectScheduleOfValuePDFRows = ({
  projectId,
  templateId,
  payload,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.addProjectScheduleOfValuePDFRows({
    projectId,
    templateId,
    payload,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: ADD_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
    payload: {
      scheduleOfValuePDFRows: data,
    },
  });
  return true;
};

export const deleteProjectScheduleOfValuePDFRows = ({
  projectId,
  templateId,
  rowId,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.deleteProjectScheduleOfValuePDFRows({
    projectId,
    templateId,
    rowId,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: DELETE_PROJECT_SCHEDULE_OF_VALUES_PDF_ROWS,
    payload: {
      scheduleOfValuePDFRows: data,
    },
  });
  return true;
};

export const getAllProjectCostcodeDetails = (projectId, query) => async (dispatch) => {
  const { data } = await ProjectsService.getAllProjectCostcodeDetails(projectId, query);
  if (!data) return false;
  dispatch({
    type: GET_ALL_PROJECT_COSTCODE_DETAILS,
    payload: {
      data,
    },
  });
  return true;
};
export const addProjectProgressHistoryFiles = (
  projectId,
  payload,
  projectName = '',
  files = [],
) => async (dispatch) => {
  const { data } = await ProjectsService.addProjectProgressHistoryFiles(projectId, payload, projectName, files);
  if (!data) return false;
  dispatch({
    type: ADD_PROJECT_PROGRESS_HISTORY_FILES,
    payload: {
      progress: data,
    },
  });
  return true;
};

export const getScheduleOfValueTemplates = () => async (dispatch) => {
  const { data } = await ProjectsService.getScheduleOfValueTemplates();
  if (!data) return false;
  dispatch({
    type: GET_SCHEDULE_OF_VALUE_TEMPLATES,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const addScheduleOfValueTemplate = (payload) => async (dispatch) => {
  const {
    data: {
      templates,
      newTemplate,
    },
  } = await ProjectsService.addScheduleOfValueTemplate(payload);
  if (!newTemplate) return false;
  dispatch({
    type: CREATE_SCHEDULE_OF_VALUE_TEMPLATE,
    payload: {
      templates,
      newTemplate,
    },
  });
  return true;
};

export const updateScheduleOfValueTemplate = (templateId, payload) => async (dispatch) => {
  const { data } = await ProjectsService.updateScheduleOfValueTemplate(templateId, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_SCHEDULE_OF_VALUE_TEMPLATE,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const deleteScheduleOfValueTemplate = (templateId) => async (dispatch) => {
  const { data } = await ProjectsService.deleteScheduleOfValueTemplate(templateId);
  if (!data) return false;
  dispatch({
    type: DELETE_SCHEDULE_OF_VALUE_TEMPLATE,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const applyScheduleOfValueTemplate = (projectId, templateId) => async (dispatch) => {
  const { data } = await ProjectsService.applyScheduleOfValueTemplate(projectId, templateId);
  if (!data) return false;
  dispatch({
    type: APPLY_SCHEDULE_OF_VALUE_TEMPLATE,
    payload: {
      ...data,
    },
  });
  return true;
};

export const previewScheduleOfValuesExcelImport = (payload) => async () => {
  const { data } = await ProjectsService.previewScheduleOfValuesExcelImport(payload);
  return data ?? null;
};

export const importScheduleOfValuesFromExcel = (payload) => async (dispatch) => {
  const { data } = await ProjectsService.importScheduleOfValuesFromExcel(payload);
  if (!data) return false;
  dispatch({
    type: APPLY_SCHEDULE_OF_VALUE_TEMPLATE,
    payload: {
      ...data,
    },
  });
  return true;
};

export const getProjectFileTemplates = () => async (dispatch) => {
  const { data } = await ProjectsService.getProjectFileTemplates();
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_FILE_TEMPLATES,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const addProjectFileTemplate = (payload) => async (dispatch) => {
  const { data } = await ProjectsService.addProjectFileTemplate(payload);
  if (!data) return false;
  dispatch({
    type: ADD_PROJECT_FILE_TEMPLATE,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const updateProjectFileTemplate = (templateId, payload) => async (dispatch) => {
  const { data } = await ProjectsService.updateProjectFileTemplate(templateId, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_PROJECT_FILE_TEMPLATE,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const deleteProjectFileTemplate = (templateId) => async (dispatch) => {
  const { data } = await ProjectsService.deleteProjectFileTemplate(templateId);
  if (!data) return false;
  dispatch({
    type: DELETE_PROJECT_FILE_TEMPLATE,
    payload: {
      templates: data,
    },
  });
  return true;
};

export const updateProjectProgressFilters = (filters) => (dispatch) => {
  dispatch({
    type: UPDATE_PROJECT_PROGRESS_FILTERS,
    payload: {
      filters,
    },
  });
};

export const getProjectGroups = () => async (dispatch) => {
  const { data } = await ProjectsService.getProjectGroups();
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_GROUPS,
    payload: {
      projectGroups: data,
    },
  });
  return true;
};

export const createProjectGroup = (payload) => async (dispatch) => {
  const { data } = await ProjectsService.createProjectGroup(payload);
  if (!data) return false;
  dispatch({
    type: CREATE_PROJECT_GROUP,
    payload: {
      projectGroups: data,
    },
  });
  return true;
};

export const updateProjectGroup = (id, payload) => async (dispatch) => {
  const { data } = await ProjectsService.updateProjectGroup(id, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_PROJECT_GROUP,
    payload: {
      projectGroups: data,
    },
  });
  return true;
};

export const deleteProjectGroup = (id) => async (dispatch) => {
  const { data } = await ProjectsService.deleteProjectGroup(id);
  if (!data) return false;
  dispatch({
    type: DELETE_PROJECT_GROUP,
    payload: {
      projectGroups: data,
    },
  });
  return true;
};

export const getProjectGroupNotes = (id) => async (dispatch) => {
  const { data } = await ProjectsService.getProjectGroupNotes(id);
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_GROUP_NOTES,
    payload: {
      id,
      data,
    },
  });
  return true;
};

export const addProjectGroupNote = ({ id, text, tags, files }) => async (dispatch) => {
  const { data } = await ProjectsService.addProjectGroupNote(id, { text, tags, files });
  if (!data) return false;
  dispatch({
    type: ADD_PROJECT_GROUP_NOTE,
    payload: data,
  });
  return true;
};

export const getProjectTypes = () => async (dispatch) => {
  const { data } = await ProjectsService.getProjectTypes();
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_TYPES,
    payload: {
      customTypes: data,
    },
  });
  return true;
};

export const getProjectCostUpdates = (id) => async (dispatch) => {
  const { data } = await ProjectsService.getProjectCostUpdates(id);
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_COST_UPDATES,
    payload: data,
  });
  return true;
};

export const getScheduleOfValueSections = ({
  projectId,
  templateId,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_READ'))) return false;
  const hasIds = !!projectId || !!templateId;
  if (!hasIds && counterWasRecentlyCalled('projects.getScheduleOfValueSections')) return true;
  if (!hasIds) projectCounters.getScheduleOfValueSections = DateTime.local();
  const { data } = await ProjectsService.getScheduleOfValueSections({
    projectId,
    templateId,
  });

  if (!data) {
    if (!hasIds) projectCounters.getScheduleOfValueSections = null;
    return false;
  }

  dispatch({
    type: GET_SCHEDULE_OF_VALUE_SECTIONS,
    payload: {
      scheduleOfValueSections: data,
    },
  });
  return true;
};

export const createScheduleOfValueSection = ({
  projectId,
  templateId,
  payload,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.createScheduleOfValueSection({
    projectId,
    templateId,
    payload,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: CREATE_SCHEDULE_OF_VALUE_SECTION,
    payload: {
      scheduleOfValueSection: data,
    },
  });
  return true;
};

export const updateScheduleOfValueSection = ({
  projectId,
  templateId,
  sectionId,
  payload,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.updateScheduleOfValueSection({
    projectId,
    templateId,
    sectionId,
    payload,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: UPDATE_SCHEDULE_OF_VALUE_SECTION,
    payload: {
      data: {
        id: sectionId,
        ...payload,
      },
    },
  });
  return true;
};

export const deleteScheduleOfValueSection = ({
  projectId,
  templateId,
  sectionId,
}) => async (dispatch) => {
  if (!(Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE'))) return false;
  const { data } = await ProjectsService.deleteScheduleOfValueSection({
    projectId,
    templateId,
    sectionId,
  });

  if (!data) {
    return false;
  }

  dispatch({
    type: DELETE_SCHEDULE_OF_VALUE_SECTION,
    payload: {
      id: sectionId,
    },
  });
  return true;
};

export const submitProjectScheduleOfValuesInvoiceHoldback = (projectId, payload) => async (dispatch) => {
  const { data: scheduleOfValues } = await ProjectsService.submitProjectScheduleOfValuesInvoiceHoldback(projectId, payload);
  if (!scheduleOfValues) return false;
  dispatch({
    type: SUBMIT_PROJECT_SCHEDULE_OF_VALUES_INVOICE_HOLDBACK,
    payload: {
      scheduleOfValues,
    },
  });
  return true;
};

export const getProgressSubContractUpdates = (id, filters) => async (dispatch) => {
  const { data } = await ProjectsService.getProgressSubContractUpdates({
    projectId: id,
    filters,
  });
  if (!data) return false;
  dispatch({
    type: GET_PROGRESS_SUBCONTRACT_UPDATES,
    payload: {
      subContractUpdates: data,
    },
  });
  return true;
};

export const getSubContracts = (id) => async (dispatch) => {
  const { data } = await ProjectsService.getSubContracts({
    projectId: id,
  });
  if (!data) return false;
  dispatch({
    type: GET_SUB_CONTRACTS,
    payload: {
      subContracts: data,
    },
  });
  return true;
};

export const updateProgressSubContract = ({
  projectId,
  payload,
  projectName = '',
  files = [],
}) => async (dispatch) => {
  const { data } = await ProjectsService.updateProgressSubContract({
    projectId,
    payload,
    projectName,
    files,
  });
  if (!data) return false;
  dispatch({
    type: UPDATE_PROGRESS_SUBCONTRACT,
    payload: {
      subContractUpdates: data,
    },
  });
  return true;
};

export const updateProgressSubContractRow = ({
  row,
  payload,
  formId,
}) => (dispatch) => {
  const updatedRow = ScheduleOfValues.getUpdatedRow({ row, payload, isProgress: true });

  dispatch({
    type: UPDATE_PROGRESS_SUBCONTRACT_ROW,
    payload: {
      key: `${formId}.${row.rowId}`,
      value: updatedRow,
    },
  });

  return true;
};

export const deleteProgressSubContractUpdate = ({
  projectId,
  rowId,
}) => async (dispatch) => {
  const { data } = await ProjectsService.deleteProgressSubContractUpdate({
    projectId,
    rowId,
  });
  if (!data) return false;
  dispatch({
    type: DELETE_PROGRESS_SUBCONTRACT_UPDATE,
    payload: {
      subContractUpdates: data,
    },
  });
  return true;
};

export const swapRowsProgressSubContract = ({
  projectId,
  updatedRows,
}) => async (dispatch) => {
  const { data } = await ProjectsService.swapRowsProgressSubContract({
    projectId,
    payload: { updatedRows },
  });
  if (!data) return false;
  dispatch({
    type: SWAP_ROW_PROGRESS_SUBCONTRACT,
    payload: {
      subContractUpdates: data,
    },
  });
  return true;
};

export const getProjectCustomFieldTemplate = () => async (dispatch) => {
  const { data: template } = await ProjectsService.getCustomFieldTemplate();
  dispatch({
    type: GET_PROJECT_CUSTOM_FIELD_TEMPLATE,
    payload: {
      template,
    },
  });
};

export const updateProjectCustomFieldTemplate = (payload) => async (dispatch) => {
  const { data: template } = await ProjectsService.updateCustomFieldTemplate(payload);
  dispatch({
    type: UPDATE_PROJECT_CUSTOM_FIELD_TEMPLATE,
    payload: {
      template,
    },
  });
};

export const getProjectCustomData = (id) => async (dispatch) => {
  if (!id) return dispatch({ type: GET_PROJECT_CUSTOM_DATA, payload: { data: [] } });

  const { data } = await ProjectsService.getCustomData(id);
  await decorateFormWithFiles({ fileMap: data.fileMap });

  if (!data) return false;
  dispatch({
    type: GET_PROJECT_CUSTOM_DATA,
    payload: {
      data,
    },
  });
  return true;
};

export const getNotes = (id) => async (dispatch) => {
  const { data } = await ProjectsService.getNotes(id);
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_NOTES,
    payload: {
      id,
      ...data,
    },
  });
  return true;
};

export const addNote = ({
  id, text, tags, files,
}) => async (dispatch) => {
  const { data } = await ProjectsService.addNote({
    id, text, tags, files,
  });
  if (!data) return false;
  dispatch({
    type: ADD_PROJECT_NOTE,
    payload: data,
  });
  return true;
};

export const geocodeProject = (id, payload) => async (dispatch) => {
  const { data } = await ProjectsService.geocodeProject(id, payload);
  if (!data) return false;
  dispatch({
    type: GEOCODE_PROJECT,
    payload: {
      id,
      data: payload,
    },
  });
  return payload;
};

export const getProjectEquipmentBucketOverrideTree = () => async (dispatch) => {
  const { data } = await ProjectsService.getProjectEquipmentBucketOverrideTree();
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_EQUIPMENT_BUCKET_OVERRIDE_TREE,
    payload: {
      data,
    },
  });
  return true;
};

export const getProjectLabels = () => async (dispatch) => {
  const { data } = await ProjectsService.getProjectLabels();
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_LABELS,
    payload: {
      projectLabels: data,
    },
  });
  return true;
};

export const getMyProjectTags = ({ hasRead = false } = {}) => async (dispatch) => {
  const { data: myProjectTags } = await ProjectsService.getMyProjectTags(hasRead);
  if (!myProjectTags) return false;
  dispatch({
    type: GET_MY_PROJECT_TAGS,
    payload: {
      myProjectTags,
      hasRead,
    },
  });
  return true;
};

export const markProjectTagsAsRead = (noteIds) => async (dispatch) => {
  const { data } = await ProjectsService.markProjectTagsAsRead(noteIds);
  if (!data) return false;
  dispatch({
    type: MARK_PROJECT_TAGS_AS_READ,
    payload: {
      noteIds: new Set(noteIds),
    },
  });
  return true;
};

export const getMyProjectGroupTags = ({ hasRead = false } = {}) => async (dispatch) => {
  const { data: myProjectGroupTags } = await ProjectsService.getMyProjectGroupTags(hasRead);
  if (!myProjectGroupTags) return false;
  dispatch({
    type: GET_MY_PROJECT_GROUP_TAGS,
    payload: {
      myProjectGroupTags,
      hasRead,
    },
  });
  return true;
};

export const markProjectGroupTagsAsRead = (noteIds) => async (dispatch) => {
  const { data } = await ProjectsService.markProjectGroupTagsAsRead(noteIds);
  if (!data) return false;
  dispatch({
    type: MARK_PROJECT_GROUP_TAGS_AS_READ,
    payload: {
      noteIds: new Set(noteIds),
    },
  });
  return true;
};

export const updateProjectFilters = (filters) => (dispatch) => {
  dispatch({
    type: UPDATE_PROJECT_FILTERS,
    payload: {
      filters,
    },
  });
};

export const getUserProjectFilterViews = () => async (dispatch) => {
  const { data } = await ProjectsService.getUserProjectFilterViews();
  if (!data) return false;
  dispatch({
    type: GET_USER_PROJECT_FILTER_VIEWS,
    payload: {
      projectFilterViews: data,
    },
  });
  return true;
};

export const addUserProjectFilterView = (payload) => async (dispatch) => {
  const {
    data: {
      newFilterView,
      filterViews,
    } = {},
  } = await ProjectsService.addUserProjectFilterView(payload);
  if (!newFilterView) return false;
  dispatch({
    type: CREATE_USER_PROJECT_FILTER_VIEW,
    payload: {
      projectFilterViews: filterViews,
    },
  });
  return newFilterView;
};

export const updateUserProjectFilterView = (filterViewId, payload) => (
  async (dispatch) => {
    const { data } = await ProjectsService.updateUserProjectFilterView(
      filterViewId,
      payload,
    );
    if (!data) return false;
    dispatch({
      type: UPDATE_USER_PROJECT_FILTER_VIEW,
      payload: {
        filterViewId,
        projectFilterViews: data,
      },
    });
    return true;
  }
);

export const deleteUserProjectFilterView = (filterViewId) => async (dispatch) => {
  const { data } = await ProjectsService.deleteUserProjectFilterView(
    filterViewId,
  );
  if (!data) return false;
  dispatch({
    type: DELETE_USER_PROJECT_FILTER_VIEW,
    payload: {
      projectFilterViews: data,
    },
  });
  return true;
};

export const favouriteUserProjectFilterView = (filterViewId, isFavourite) => async (dispatch) => {
  const { data } = await ProjectsService.favouriteUserProjectFilterView(
    filterViewId,
    isFavourite,
  );
  if (!data) return false;
  dispatch({
    type: FAVOURITE_USER_PROJECT_FILTER_VIEW,
    payload: {
      projectFilterViews: data,
    },
  });
  return true;
};

export const fetchFilteredProjectIds = (filters) => async (dispatch) => {
  // If no filters provided, clear filtered project IDs
  if (!filters) {
    dispatch({
      type: SET_FILTERED_PROJECT_IDS,
      payload: {
        filteredProjectIds: null,
      },
    });
    return null;
  }

  const { data } = await ProjectsService.fetchFilteredProjectIds(filters);
  if (!data) return null;
  dispatch({
    type: SET_FILTERED_PROJECT_IDS,
    payload: {
      filteredProjectIds: data.projectIds,
    },
  });
  return data.projectIds;
};

export const getProjectCustomCategoryEstimates = (query) => async (dispatch) => {
  const { data } = await ProjectsService.getCustomCategoryEstimates(query);
  if (!data) return false;
  dispatch({
    type: GET_PROJECT_CUSTOM_CATEGORY_ESTIMATES,
    payload: {
      projectId: query.projectId,
      data,
    },
  });
  return true;
};
